import {
  CaretRightOutlined,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  LoadingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Flex,
  Input,
  List,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useLazyQueryWithCallbacks from '../../../../../../common/CommonUseLazyQuery';
import useRouter from '../../../../../../common/useRouter';
import { PROJECT_REGISTER_NOMENCLATURE_LIST } from '../../../../graphql/Queries';

const { Text } = Typography;
const Loader = <LoadingOutlined spin />;

const findPathFromValue = (treeData, value) => {
  const valueString =
    typeof value === 'object' && value !== null ? value?.label : value;
  if (!valueString || !treeData || treeData?.length === 0) {
    return [];
  }
  const parts = valueString?.split(' / ');
  let currentLevelItems = treeData;
  const path = [];
  parts?.forEach((part) => {
    const matchingNode = currentLevelItems?.find(
      (node) => node?.title === part,
    );
    if (matchingNode) {
      path?.push(matchingNode);
      currentLevelItems = matchingNode?.children || [];
    } else {
      path?.push({ title: part, key: part, children: [], isCustom: true });
      currentLevelItems = [];
    }
  });
  return path;
};

const CustomNomenclatureDropdown = ({
  value,
  onChange,
  isFieldDisabled,
  nomenclatureStatus,
  popupContainer,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [path, setPath] = useState([]);
  const [viewingIndex, setViewingIndex] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [inputError, setInputError] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isDisabled = isFieldDisabled || !nomenclatureStatus;
  const {
    params: { projectId },
  } = useRouter();

  useEffect(() => {
    if (scrollContainerRef?.current) {
      const element = scrollContainerRef?.current;
      if (element) {
        element.scrollTop = element?.scrollHeight;
      }
    }
  }, [path, isOpen]);

  const transformDataForTreeSelect = (node) => {
    if (node?.isActive === false) {
      return null;
    }
    const treeNode = {
      title: node?.name,
      value: node?.id,
      key: node?.id,
      refId: node?.id,
    };
    if (node?.data?.length > 0) {
      treeNode.children = node?.data
        ?.map(transformDataForTreeSelect)
        ?.filter(Boolean);
    } else {
      treeNode.children = [];
    }
    return treeNode;
  };
  const [getProjectRegisterNomenclatureList, { loading }] =
    useLazyQueryWithCallbacks(PROJECT_REGISTER_NOMENCLATURE_LIST, {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        if (res?.projectRegisterNomenclatureList) {
          const rootNode = res?.projectRegisterNomenclatureList;
          const transformedData = transformDataForTreeSelect(rootNode);
          setTreeData(transformedData ? [transformedData] : []);
        } else {
          setTreeData([]);
        }
      },
      onError: () => {
        setTreeData([]);
      },
    },
  );

  const onOpen = useCallback(() => {
    getProjectRegisterNomenclatureList({
      variables: { filter: { projectId } },
    });
  }, [getProjectRegisterNomenclatureList, projectId]);

  const currentItems = useMemo(() => {
    if (viewingIndex !== null) {
      if (viewingIndex === -1) return treeData;
      return path?.[viewingIndex]?.children || [];
    }
    const lastNode = path?.[path?.length - 1];
    return lastNode?.children || (path?.length === 0 ? treeData : []);
  }, [viewingIndex, path, treeData]);

  const filteredItems = useMemo(() => {
    if (!currentItems) return [];
    const validItems = currentItems?.filter(Boolean);
    if (!searchText) return validItems;
    return validItems?.filter((item) =>
      item?.title?.toLowerCase()?.includes(searchText?.toLowerCase()),
    );
  }, [currentItems, searchText]);

  const exactMatchExists = useMemo(() => {
    if (!searchText) return false;
    return filteredItems?.some(
      (item) =>
        item?.title?.toLowerCase() === searchText?.trim()?.toLowerCase(),
    );
  }, [filteredItems, searchText]);

  useEffect(() => {
    if (isOpen && treeData?.length > 0) {
      if (value) {
        setPath(findPathFromValue(treeData, value));
      } else if (treeData?.length === 1) {
        setPath([treeData?.[0]]);
      } else {
        setPath([]);
      }
      setSearchText('');
      setInputError(null);
      setViewingIndex(null);
    }
  }, [treeData, isOpen, value]);

  const clearError = () => {
    if (inputError) setInputError(null);
  };
  const getCustomTail = (currentPath) => {
    const lastStandardIndex = currentPath?.findLastIndex(
      (node) => !node?.isCustom,
    );
    return currentPath?.slice(lastStandardIndex + 1);
  };

  const handleBreadcrumbClick = (index) => {
    const item = path?.[index];
    if (item?.isCustom) {
      setSearchText(item?.title);
      setViewingIndex(index);
    } else {
      setSearchText('');
      if (index === path?.length - 1) {
        setViewingIndex(null);
      } else {
        setViewingIndex(index);
      }
    }

    clearError();
    inputRef?.current?.focus();
  };

  const handleItemClick = (item) => {
    const rawTail = getCustomTail(path);
    let newPath;
    let nextViewIndex = null;
    if (viewingIndex !== null) {
      const basePath =
        viewingIndex === -1 ? [] : path?.slice(0, viewingIndex + 1);
      const safeTail = rawTail?.filter(
        (t) =>
          !basePath?.some((b) => b?.key === t?.key) && t?.key !== item?.key,
      );
      newPath = [...basePath, item, ...safeTail];
      nextViewIndex = viewingIndex + 1;
    } else {
      newPath = [...path, item];
    }
    setPath(newPath);
    setViewingIndex(nextViewIndex);
    setSearchText('');
    clearError();
    inputRef?.current?.focus();
  };

  const handleRemoveCustomTag = (e, index) => {
    e?.preventDefault();
    e?.stopPropagation();
    setPath(path?.slice(0, index));
    setViewingIndex(null);
    setSearchText('');
    inputRef?.current?.focus();
  };

  const handleAddCustomSegment = () => {
    if (!searchText?.trim()) return;
    if (viewingIndex !== null && path?.[viewingIndex]?.isCustom) {
      const updatedPath = [...path];
      updatedPath[viewingIndex] = {
        ...updatedPath?.[viewingIndex],
        title: searchText?.trim(),
      };
      setPath(updatedPath);
      if (viewingIndex < updatedPath?.length - 1) {
        const nextIndex = viewingIndex + 1;
        setViewingIndex(nextIndex);
        if (updatedPath?.[nextIndex]?.isCustom) {
          setSearchText(updatedPath?.[nextIndex]?.title);
        } else {
          setSearchText('');
        }
      } else {
        setViewingIndex(null);
        setSearchText('');
      }
    } else {
      const newCustomNode = {
        title: searchText?.trim(),
        key: `custom-${Date?.now()}`,
        children: [],
        isCustom: true,
      };
      setPath([...path, newCustomNode]);
      setViewingIndex(null);
      setSearchText('');
    }
    clearError();
    inputRef?.current?.focus();
  };

  const handleDone = () => {
    const finalPath = [...path];
    if (searchText?.trim()) {
      if (viewingIndex !== null && path?.[viewingIndex]?.isCustom) {
        finalPath[viewingIndex] = {
          ...finalPath?.[viewingIndex],
          title: searchText?.trim(),
        };
      } else {
        const newCustomNode = {
          title: searchText?.trim(),
          key: `custom-${Date?.now()}`,
          children: [],
          isCustom: true,
        };
        finalPath?.push(newCustomNode);
      }
    }
    const finalPathString = finalPath?.map((p) => p?.title)?.join(' / ');
    const lastRealNode = [...finalPath]
      ?.reverse()
      ?.find((node) => !node?.isCustom);
    const nodeId = lastRealNode
      ? lastRealNode?.refId || lastRealNode?.value
      : null;
    const payload = {
      label: finalPathString,
      value: nodeId,
    };
    onChange(payload);
    setIsOpen(false);
    setSearchText('');
    setInputError(null);
    setViewingIndex(null);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setInputError(null);
    setViewingIndex(null);
  };

  const handleOpenChange = (visible) => {
    if (visible) {
      onOpen();
      if (value && treeData) {
        setPath(findPathFromValue(treeData, value));
      } else {
        setPath(treeData?.length === 1 ? [treeData?.[0]] : []);
      }
      setSearchText('');
      setInputError(null);
      setIsOpen(true);
      setViewingIndex(null);
      inputRef?.current?.focus();
    } else {
      const currentPathString = path?.map((p) => p?.title)?.join(' / ') || '';
      const savedValueString =
        (typeof value === 'object' && value !== null ? value?.label : value) ||
        '';
      const isDefaultAutoSelect =
        !savedValueString &&
        treeData?.length === 1 &&
        path?.length === 1 &&
        path?.[0]?.key === treeData?.[0]?.key;
      const isSelectionChanged =
        currentPathString !== savedValueString && !isDefaultAutoSelect;
      const hasPendingText = !!searchText?.trim();
      if (isSelectionChanged || hasPendingText) {
        setInputError('Please create a custom name or apply selection.');
        setTimeout(() => {
          inputRef?.current?.focus({ cursor: 'end' });
        }, 100);
      } else {
        setIsOpen(false);
        setInputError(null);
        setViewingIndex(null);
      }
    }
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onChange(undefined);
    setPath([]);
    setSearchText('');
    setViewingIndex(null);
  };

  const renderDropdownContent = () => {
    const showAddButton = searchText?.trim() && !exactMatchExists;
    let highlightedItemId = null;
    if (viewingIndex !== null) {
      const targetIndex = viewingIndex + 1;
      if (targetIndex < path?.length) {
        highlightedItemId = path?.[targetIndex]?.key;
      }
    }

    const renderPanelContent = () => {
      if (loading) {
        return (
          <div className="loading-container">
            <Spin indicator={Loader} />
          </div>
        );
      }

      return (
        <Space orientation="vertical" className="width-percent-100" size={0}>
          <div className="search-container">
            <Flex gap={8}>
              <Input
                ref={inputRef}
                className="width-percent-100"
                autoFocus
                status={inputError ? 'error' : ''}
                value={searchText}
                onChange={(e) => {
                  const val = (e?.target?.value || '')
                    ?.trimStart()
                    ?.replace(/  +/g, ' ');
                  setSearchText(val);
                  clearError();
                }}
                maxLength={40}
                onPressEnter={
                  showAddButton ? handleAddCustomSegment : undefined
                }
                placeholder={
                  path?.length === 0 || viewingIndex === -1
                    ? 'Search root location...'
                    : 'Search or add custom...'
                }
                prefix={<SearchOutlined className="search-icon" />}
                allowClear
              />
              <Button
                type="primary"
                onClick={handleAddCustomSegment}
                className="add-custom-btn"
                disabled={!showAddButton}
              >
                /
              </Button>
            </Flex>
            {inputError && <span className="error-text">{inputError}</span>}
          </div>

          <div className="list-container">
            {filteredItems?.length > 0 && (
              <List
                dataSource={filteredItems}
                rowKey={(item) => item?.key || item?.value}
                renderItem={(item) => {
                  const isHighlighted = item?.key === highlightedItemId;
                  return (
                    <div
                      key={item?.key || item?.value}
                      onClick={() => handleItemClick(item)}
                      className={`list-item ${isHighlighted ? 'item-highlighted' : ''}`}
                    >
                      <div className="list-item-content">
                        <Text className="item-title" strong={isHighlighted}>
                          {item?.title}
                        </Text>
                        {isHighlighted ? (
                          <CheckOutlined />
                        ) : (
                          <CaretRightOutlined className="item-arrow" />
                        )}
                      </div>
                    </div>
                  );
                }}
              />
            )}

            {filteredItems?.length === 0 && !searchText && (
              <div className="empty-state">
                Type above to create custom name
              </div>
            )}
            {filteredItems?.length === 0 && searchText && (
              <div className="empty-state">
                No result found, please create custom name
              </div>
            )}
          </div>

          <div className="footer">
            <Button
              type="primary"
              onClick={handleDone}
              disabled={
                exactMatchExists ||
                (!searchText?.trim() && !path?.some((item) => item?.isCustom))
              }
              block
            >
              Create
            </Button>
          </div>
        </Space>
      );
    };

    return (
      <div className="custom-nomenclature-dropdown">
        <div className="header-row thin-scrollbar" ref={scrollContainerRef}>
          <div className="breadcrumb-scroll">
            {path?.length > 0 ? (
              <div className="custom-breadcrumb-container">
                {path?.map((item, index) => {
                  const isActiveView = viewingIndex === index;
                  const isLast = index === path?.length - 1;
                  return (
                    <div key={item?.key || index} className="breadcrumb-unit">
                      <div
                        className={`custom-tag ${!item?.isCustom ? 'nomenclature-tag' : ''} ${
                          isActiveView ? 'active-tag' : ''
                        }`}
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleBreadcrumbClick(index);
                        }}
                      >
                        <span
                          className={`breadcrumb-item-text ${isActiveView ? 'breadcrumb-active-item' : ''}`}
                        >
                          {item?.title}
                        </span>
                        {isLast && (
                          <div className="close-icon-wrapper">
                            <CloseOutlined
                              className="close-icon"
                              onClick={(e) => handleRemoveCustomTag(e, index)}
                            />
                          </div>
                        )}
                      </div>
                      {!isLast && <span className="separator">/</span>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text
                strong
                onClick={() => {
                  setViewingIndex(-1);
                  setSearchText('');
                  inputRef?.current?.focus();
                }}
                className="placeholder-text"
              >
                Select Location
              </Text>
            )}
          </div>
          <div className="close-btn-container">
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleCancel}
              className="close-btn"
            />
          </div>
        </div>

        {renderPanelContent()}
      </div>
    );
  };

  const valueString =
    typeof value === 'object' && value !== null ? value?.label : value;

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={handleOpenChange}
      popupRender={renderDropdownContent}
      trigger={['click']}
      classNames={{ root: 'nomenclature-dropdown-overlay' }}
      destroyOnHidden
      disabled={isDisabled}
      // eslint-disable-next-line no-undef
      getPopupContainer={popupContainer || (() => document?.body)}
    >
      <Tooltip
        title={
          !nomenclatureStatus
            ? 'Set up nomenclature to activate this field option. else use text field'
            : valueString
        }
      >
        <Input
          readOnly
          disabled={isDisabled}
          value={valueString}
          placeholder="Select location..."
          suffix={
            value && !isDisabled ? (
              <CloseCircleFilled
                onClick={handleClear}
                className="nomenclature-input-clear-icon"
              />
            ) : null
          }
        />
      </Tooltip>
    </Dropdown>
  );
};

export default CustomNomenclatureDropdown;
